import React, { useEffect, useState } from "react";
import {
  Calendar,
  Clock,
  CheckCircle,
  CheckSquare,  // ✔ confirmed icon
  Users         // ✔ registered users icon
} from "lucide-react";
import "./AdminDashboard.css";
import { getAllUsers } from "../api";

const AdminDashboard = () => {
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [users, setUsers] = useState([]);

  // Load bookings + users
  useEffect(() => {
    const storedBookings = JSON.parse(localStorage.getItem("bookings")) || [];

    const uniqueBookings = storedBookings.filter(
      (booking, index, self) =>
        index ===
        self.findIndex(
          (b) =>
            b.customerEmail === booking.customerEmail &&
            b.serviceType === booking.serviceType
        )
    );

    setBookings(uniqueBookings);

    // Load users from MongoDB
    async function loadUsers() {
      try {
        const response = await getAllUsers();
        setUsers(response.data);
      } catch (err) {
        console.error("Failed to load users", err);
      }
    }

    loadUsers();
  }, []);

  // Booking Summary
  const totalBookings = bookings.length;
  const pendingApprovals = bookings.filter(
    (b) => b.status?.toLowerCase() === "pending approval"
  ).length;
  const confirmed = bookings.filter(
    (b) => b.status?.toLowerCase() === "confirmed"
  ).length;
  const completed = bookings.filter(
    (b) => b.status?.toLowerCase() === "completed"
  ).length;

  const totalUsers = users.length;

  // Approve booking
  const handleApprove = (index) => {
    const updated = [...bookings];
    updated[index].status = "Confirmed";
    localStorage.setItem("bookings", JSON.stringify(updated));
    setBookings(updated);
  };

  // Complete booking
  const handleComplete = (index) => {
    const updated = [...bookings];
    updated[index].status = "Completed";
    localStorage.setItem("bookings", JSON.stringify(updated));
    setBookings(updated);
  };

  return (
    <div className="admin-dashboard">
      {/* HEADER */}
      <header className="dashboard-header">
        <div className="header-left">
          <h1>RepairMate Admin</h1>
          <p className="welcome-text">Welcome back, admin@gmail.com</p>
        </div>
        <div className="header-right">
          <span className="admin-role">Admin</span>
          <button className="logout-btn" onClick={() => alert("Logout clicked")}>
            Logout
          </button>
        </div>
      </header>

      <div className="dashboard-container">
        {/* SIDEBAR */}
        <aside className="sidebar">
          <ul className="nav-menu">
            <li className="nav-item active">
              <button>
                <Calendar size={16} /> Dashboard
              </button>
            </li>
            <li className="nav-item">
              <button>
                <Clock size={16} /> Bookings
              </button>
            </li>
            <li className="nav-item">
              <button>
                <Users size={16} /> Users
              </button>
            </li>
          </ul>
        </aside>

        {/* MAIN CONTENT */}
        <main className="main-content">
          {/* STAT CARDS */}
          <div className="stats-grid">

            <div className="stat-card">
              <div className="stat-icon total-bookings">
                <Calendar size={24} />
              </div>
              <div className="stat-info">
                <h3>{totalBookings}</h3>
                <p>Total Bookings</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon pending">
                <Clock size={24} />
              </div>
              <div className="stat-info">
                <h3>{pendingApprovals}</h3>
                <p>Pending Approvals</p>
              </div>
            </div>

            {/* ✔ FIXED CONFIRMED ICON (CheckSquare) */}
            <div className="stat-card">
              <div className="stat-icon confirmed">
                <CheckSquare size={24} />
              </div>
              <div className="stat-info">
                <h3>{confirmed}</h3>
                <p>Confirmed</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon completed">
                <CheckCircle size={24} />
              </div>
              <div className="stat-info">
                <h3>{completed}</h3>
                <p>Completed</p>
              </div>
            </div>

            {/* ✔ FIXED REGISTERED USERS ICON (Users) */}
            <div className="stat-card">
              <div className="stat-icon users-stat">
                <Users size={24} />
              </div>
              <div className="stat-info">
                <h3>{totalUsers}</h3>
                <p>Registered Users</p>
              </div>
            </div>

          </div>

          {/* CONTENT */}
          <div className="content-grid">
            
            {/* BOOKINGS SECTION */}
            <div className="recent-bookings">
              <h2>Recent Bookings</h2>
              {bookings.length === 0 ? (
                <p>No bookings yet.</p>
              ) : (
                bookings.map((booking, index) => (
                  <div key={index} className="booking-item">
                    <div className="booking-info">
                      <h4>{booking.customerName}</h4>
                      <p>{booking.serviceType} – {booking.date}</p>
                      <p className="booking-address">{booking.address}</p>
                    </div>

                    <div className="booking-meta">
                      <span
                        className={`status-badge ${booking.status
                          ?.toLowerCase()
                          .replace(/\s/g, "-")}`}
                      >
                        {booking.status}
                      </span>

                      <div className="booking-actions">
                        <button
                          className="btn-outline"
                          onClick={() => setSelectedBooking(booking)}
                        >
                          Show More
                        </button>

                        {booking.status === "Pending Approval" && (
                          <button
                            className="btn-approve"
                            onClick={() => handleApprove(index)}
                          >
                            Approve
                          </button>
                        )}

                        {booking.status === "Confirmed" && (
                          <button
                            className="btn-complete"
                            onClick={() => handleComplete(index)}
                          >
                            Complete
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* USERS SECTION */}
            <div className="recent-users">
              <h2>Registered Users</h2>

              {users.length === 0 ? (
                <p>No users registered yet.</p>
              ) : (
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Signup Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u._id}>
                        <td>{u.name}</td>
                        <td>{u.email}</td>
                        <td>{new Date(u.createdAt).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

          </div>
        </main>
      </div>

      {/* MODAL */}
      {selectedBooking && (
        <div className="modal-overlay" onClick={() => setSelectedBooking(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Booking Details</h3>
            <p><strong>Name:</strong> {selectedBooking.customerName}</p>
            <p><strong>Email:</strong> {selectedBooking.customerEmail}</p>
            <p><strong>Phone:</strong> {selectedBooking.customerPhone}</p>
            <p><strong>Service:</strong> {selectedBooking.serviceType}</p>
            <p><strong>Issue:</strong> {selectedBooking.issue}</p>
            <p><strong>Address:</strong> {selectedBooking.address}</p>
            <p><strong>Date:</strong> {selectedBooking.date}</p>
            <p><strong>Time:</strong> {selectedBooking.time}</p>
            <p><strong>Status:</strong> {selectedBooking.status}</p>
            <p><strong>Price:</strong> ₹{selectedBooking.price}</p>

            <button className="btn-close" onClick={() => setSelectedBooking(null)}>
              Close
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminDashboard;
